/**
 * ApplicantProcess Component
 *
 * This component handles the multi-step applicant registration process.
 * The process is divided into three steps, with each step corresponding to a different
 * component:
 * 1. ApplicantLandingStepOne - Select a program.
 * 2. ApplicantLandingStepTwo - Provide personal and educational information.
 * 3. ApplicantLandingStepThree - Provide additional details and finalize registration.
 *
 * State Management:
 * The applicant's data is stored in a single state object (`applicant`) that is passed
 * down to each step component. This state includes fields like `username`, `email`,
 * `program`, and various other fields collected throughout the registration process.
 * The `setApplicant` function is used to update the applicant's data as they move
 * through the steps.
 *
 * Navigation:
 * React Router is used to navigate between the steps. The `useNavigate` hook from
 * `react-router-dom` is used to programmatically navigate between steps. The routes
 * are defined within the `Routes` component, and each route corresponds to one of
 * the step components.
 *
 * - `goToStepOne()`, `goToStepTwo()`, and `goToStepThree()` are helper functions
 *   used to navigate to the respective steps.
 *
 * Routing Logic:
 * - The default route ("/applicant/") redirects to the first step ("/applicant/step1").
 * - Each step is mapped to a specific route ("/step1", "/step2", "/step3").
 * - When the user completes the final step (step 3), an alert is currently triggered
 *   (this can be replaced with any final action, such as navigating to a different
 *   page or dashboard).
 *
 * Custom Claims and User Role:
 * During the third step, the user’s role is updated (e.g., from "applicant" to "freetrial")
 * via Firebase custom claims (handled in the respective step component). This role
 * change is important for controlling access to specific routes or pages, such as
 * redirecting the user to the learner dashboard after registration.
 *
 * Future Enhancements:
 * Additional steps can be easily added by appending new routes and corresponding
 * components. Error handling, form validation, and additional state management
 * improvements can also be implemented as the application grows.
 */
import React, { useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import ApplicantLandingStepOne from "../components/applicationProcess/ApplicantLandingStepOne";
import ApplicantLandingStepTwo from "../components/applicationProcess/ApplicantLandingStepTwo";
import ApplicantLandingStepThree from "../components/applicationProcess/ApplicantLandingStepThree";

const ApplicantProcess = () => {
  const [applicant, setApplicant] = useState({
    username: "", // From initial registration or user data
    email: "",
    program: "",
    // Step 2 fields
    whyInterested: "",
    codingExperience: "",
    education: "",
    age: "",
    gender: "",
    country: "",
    phoneNumber: "",
    // Step 3 fields
    studyMode: "",
    studyHours: "",
    timeCommitment: "",
    paymentMethod: "",
    startDate: "",
  });

  const navigate = useNavigate();

  // Navigation functions
  const goToStepTwo = () => {
    navigate("/applicant/step2");
  };

  const goToStepThree = () => {
    navigate("/applicant/step3");
  };

  const goToStepOne = () => {
    navigate("/applicant/step1");
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/applicant/step1" />} />
      <Route
        path="/step1"
        element={
          <ApplicantLandingStepOne
            applicant={applicant}
            setApplicant={setApplicant}
            goToNextStep={goToStepTwo}
          />
        }
      />
      <Route
        path="/step2"
        element={
          <ApplicantLandingStepTwo
            applicant={applicant}
            setApplicant={setApplicant}
            goToNextStep={goToStepThree}
            goBack={goToStepOne}
          />
        }
      />
      <Route
        path="/step3"
        element={
          <ApplicantLandingStepThree
            applicant={applicant}
            setApplicant={setApplicant}
            goToNextStep={() => alert("Process Complete!", applicant)} // Adjust as needed
            goBack={goToStepTwo}
          />
        }
      />
      {/* Add more steps here as needed */}
    </Routes>
  );
};

export default ApplicantProcess;
